import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Breadcrumbs, Button, Card, EmptyState } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang, type Lang } from '../../i18n';
import { get } from '../../services/api';
import './ServiceCategoryPage.scss';

interface Service { slug: string; title: string; short_description?: string; description?: string; bullets?: string[] }
interface Category { slug: string; title: string; description?: string }

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

export default function ServiceCategoryPage() {
  const { t, lang } = useLang();
  const { category = '' } = useParams();
  const [cat, setCat] = useState<Category | null>(null);
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const ls = LANG_MAP[lang];
        const c = await get<Category>(`/services/categories/${category}/?lang=${ls}`);
        const list = await get<{ results?: Service[]; items?: Service[] }>(`/services/?category=${category}&lang=${ls}&page_size=100`);
        if (cancel) return;
        setCat(c || { slug: category, title: category });
        setServices(list?.results || list?.items || []);
      } catch {
        if (!cancel) setCat({ slug: category, title: category });
      }
    })();
    return () => { cancel = true; };
  }, [category, lang]);

  const title = cat?.title || category;

  useDocumentHead({
    title: `${title} — ${t('services.title')} — ${t('brand.name')}`,
    description: cat?.description || t('services.sub'),
    canonical: `https://ukam.uz/services/${category}`,
    og: { title, description: cat?.description || t('services.sub'), type: 'website' },
    schemaOrg: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('brand.name'), item: 'https://ukam.uz/' },
      { '@type': 'ListItem', position: 2, name: t('services.title'), item: 'https://ukam.uz/services' },
      { '@type': 'ListItem', position: 3, name: title, item: `https://ukam.uz/services/${category}` },
    ] },
  });

  return (
    <div className="service-cat">
      <div className="container service-cat__head">
        <Breadcrumbs items={[
          { label: t('brand.name'), href: '/' },
          { label: t('services.title'), href: '/services' },
          { label: title },
        ]} />
        <h1 className="service-cat__title">{title}</h1>
        {cat?.description && <p className="service-cat__desc">{cat.description}</p>}
      </div>
      <div className="container service-cat__list">
        {services.length === 0 ? (
          <EmptyState title={t('services.empty')} />
        ) : (
          <div className="grid grid--3 service-cat__grid">
            {services.map((s) => (
              <Card key={s.slug} variant="elevated" padding="md" className="service-cat__card">
                <h3 className="service-cat__card-title">{s.title}</h3>
                {(s.short_description || s.description) && <p className="service-cat__card-desc">{s.short_description || s.description}</p>}
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="service-cat__bullets">
                    {s.bullets.slice(0, 3).map((b, i) => (
                      <li key={i}><Check size={14} /><span>{b}</span></li>
                    ))}
                  </ul>
                )}
                <Link to={`/services/${category}/${s.slug}`} className="service-cat__more">
                  <Button variant="ghost" size="sm" iconRight={<ArrowRight size={14} />}>{t('services.details')}</Button>
                </Link>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
